import { useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FeedbackMessage } from "@/components/ui/feedback";
import { FieldError, Label } from "@/components/ui/input";
import { useAuth } from "@/features/auth/useAuth";
import {
  uploadDocument,
  type DocumentKind,
} from "@/features/documents/repo";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const kindLabel: Record<DocumentKind, string> = {
  bol: "BOL",
  pod: "POD",
  cdl: "CDL",
  medical_card: "Medical card",
  insurance: "Insurance",
  w9: "W-9",
  resume: "Resume",
  other: "Other",
};

interface DocumentUploadCardProps {
  title: string;
  description: string;
  relatedReference: string;
  kinds: DocumentKind[];
  defaultKind?: DocumentKind;
  onUploaded?: () => void;
}

export function DocumentUploadCard({
  title,
  description,
  relatedReference,
  kinds,
  defaultKind,
  onUploaded,
}: DocumentUploadCardProps) {
  const { user } = useAuth();
  const [kind, setKind] = useState<DocumentKind>(defaultKind ?? kinds[0] ?? "other");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onFileChange(nextFile: File | null) {
    setError(null);
    setSuccess(null);
    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(nextFile.type)) {
      setFile(null);
      setError("Upload a PDF, Word document, JPG, or PNG.");
      return;
    }
    if (nextFile.size > MAX_BYTES) {
      setFile(null);
      setError("That file is over 10 MB. Please upload a smaller file.");
      return;
    }
    setFile(nextFile);
  }

  async function handleUpload() {
    if (!user) {
      setError("Please sign in again before uploading.");
      return;
    }
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await uploadDocument({ file, kind, relatedReference, owner: user });
      setFile(null);
      setSuccess("Document uploaded for staff review.");
      onUploaded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not upload the document.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-brand-500">{description}</p>
        <div>
          <Label htmlFor={`${relatedReference}-document-kind`}>Document type</Label>
          <select
            id={`${relatedReference}-document-kind`}
            value={kind}
            onChange={(e) => setKind(e.target.value as DocumentKind)}
            className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
          >
            {kinds.map((option) => (
              <option key={option} value={option}>
                {kindLabel[option]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={`${relatedReference}-document-file`}>File</Label>
          <input
            id={`${relatedReference}-document-file`}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            className="block w-full rounded-md border border-brand-200 bg-white px-2 py-2 text-sm file:mr-2 file:rounded-md file:border-0 file:bg-brand-100 file:px-2 file:py-1.5 file:text-sm file:font-medium file:text-brand-800 sm:px-3 sm:file:mr-3 sm:file:px-3"
          />
          {file && (
            <p className="mt-1 text-xs text-brand-400">
              Selected: <span className="break-all">{file.name}</span> ({formatBytes(file.size)})
            </p>
          )}
        </div>
        <FieldError>{error}</FieldError>
        <FeedbackMessage tone="success">{success}</FeedbackMessage>
        <Button variant="accent" className="w-full" onClick={handleUpload} disabled={busy}>
          <Upload className="mr-1 h-4 w-4" /> {busy ? "Uploading..." : "Upload document"}
        </Button>
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
